import { Col, Row, Table, Tag } from "antd";
import type { TableProps } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DonutChart from "../components/Charts/DonutChart";
import TailSpinLoader from "../components/Items/Loaders/TailSpinLoader";
import NetwalaSelect from "../components/Items/Select/Index";
import useAxiosPost from "../hooks/useAxiosPost";
import useMultiNotification from "../hooks/useNotification";
import { formatDataInYYYYMMDDFormatWithTime } from "../helpers/date";
import { statusColors, priorityColors } from "../helpers/tickets";
import "./ComplaintsReportsPage.css";

interface ComplaintStatus {
  status: string;
  statusLabel: string;
  count: number;
  percentage: string;
  colorCode: string;
}

const ComplaintsReportsPage = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [dateFilter, setDateFilter] = useState<string>("today");
  const [linemanFilter, setLinemanFilter] = useState<string>("all");

  // Redirect /reports to /reports/complaints
  useEffect(() => {
    if (location.pathname === "/reports") {
      navigate("/reports/complaints", { replace: true });
    }
  }, [location.pathname, navigate]);

  const [
    getComplaintsByDateRange,
    complaintsByDateRangeResponse,
    complaintsByDateRangeError,
    complaintsByDateRangeLoading,
  ] = useAxiosPost<ResponseProp>("reports/getComplaintsByDateRange");

  const [
    getLast5Complaints,
    last5ComplaintsResponse,
    last5ComplaintsError,
    last5ComplaintsLoading,
  ] = useAxiosPost<ResponseProp>("reports/getLast5Complaints");

  const [complaintsData, setComplaintsData] = useState<ComplaintStatus[]>([]);
  const [last5Complaints, setLast5Complaints] = useState<ITicket[]>([]);

  const dateFilterOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "7 Days", value: "7 days" },
    { label: "30 Days", value: "30 days" },
    { label: "90 Days", value: "90 days" },
  ];

  const linemanFilterOptions = [
    { label: "All Lineman", value: "all" },
    // Add more lineman options if needed
  ];

  // Fetch last 5 complaints only once on component mount
  useEffect(() => {
    getLast5Complaints({});
  }, []);

  // Fetch complaints by date range whenever dateFilter changes
  useEffect(() => {
    getComplaintsByDateRange({ dateFilter });
  }, [dateFilter]);

  useEffect(() => {
    if (complaintsByDateRangeResponse !== null) {
      const data: any = complaintsByDateRangeResponse.responseData;
      if (data?.complaints) {
        setComplaintsData(data.complaints);
      }
    }
  }, [complaintsByDateRangeResponse]);

  useEffect(() => {
    if (last5ComplaintsResponse !== null) {
      const data: any = last5ComplaintsResponse.responseData;
      if (data?.complaints) {
        setLast5Complaints(data.complaints);
      }
    }
  }, [last5ComplaintsResponse]);

  useEffect(() => {
    if (complaintsByDateRangeError) {
      openNotification("error", "Error", complaintsByDateRangeError);
    }
  }, [complaintsByDateRangeError]);

  useEffect(() => {
    if (last5ComplaintsError) {
      openNotification("error", "Error", last5ComplaintsError);
    }
  }, [last5ComplaintsError]);

  // Prepare chart data - use counts instead of percentages
  const chartLabels = complaintsData.map((item) => item.statusLabel);
  const chartSeries = complaintsData.map((item) => item.count);
  const chartColors = complaintsData.map((item) => item.colorCode);

  // Table columns - matching SupportTicketsTable structure
  const columns: TableProps<ITicket>["columns"] = [
    {
      title: "ID",
      dataIndex: "ticketId",
      key: "ticketId",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag color={statusColors[text as keyof typeof statusColors]}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (text) => (
        <Tag color={priorityColors[text as keyof typeof priorityColors]}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (
        <span>{formatDataInYYYYMMDDFormatWithTime(new Date(text))}</span>
      ),
    },
    {
      title: "Connection Details",
      render: (record) => (
        <span>
          {record.connectionFullName} - {record.connectionPhoneNumber}
        </span>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
    },
    {
      title: "Assigned To",
      dataIndex: "assignedToName",
      key: "assignedToName",
      render: (text) => <span>{text ? text : "Unassigned"}</span>,
    },
  ];

  return (
    <>
      {contextHolder}
      <div className="complaints-reports-page">
        {complaintsByDateRangeLoading || last5ComplaintsLoading ? (
          <TailSpinLoader />
        ) : (
          <>
            <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
              <Col span={12}>
                <div className="chart-container">
                  {complaintsData.length > 0 ? (
                    <DonutChart
                      labels={chartLabels}
                      series={chartSeries}
                      colors={chartColors}
                      totalLabel="Count"
                    />
                  ) : (
                    <div className="no-data">No complaints data available</div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div className="filters-container">
                  <div className="filter-item">
                    <label>Date Filter</label>
                    <NetwalaSelect
                      value={dateFilter}
                      onChange={(value) => setDateFilter(value)}
                      options={dateFilterOptions}
                      size="large"
                      height="45px"
                    />
                  </div>
                  <div className="filter-item">
                    <label>Lineman Filter</label>
                    <NetwalaSelect
                      value={linemanFilter}
                      onChange={(value) => setLinemanFilter(value)}
                      options={linemanFilterOptions}
                      size="large"
                      height="45px"
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <div className="table-container">
                  <h3 className="table-title">Last 5 Complaints</h3>
                  <Table
                    dataSource={last5Complaints}
                    columns={columns}
                    pagination={false}
                    rowKey="_id"
                    className="netwala-users-table striped-table"
                    scroll={{ x: "max-content" }}
                    onRow={(record) => {
                      return {
                        onClick: () => {
                          navigate(`/ticket-details/${record._id}`);
                        },
                      };
                    }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default ComplaintsReportsPage;
